import { cloneElement, useMemo, useState } from 'react';
import { Alert, Autocomplete, Box, ListSubheader, SvgIcon, TextField, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ReportNavItem } from '../../Reporting/components/shared/ReportTypesData';
import { ReportType } from '../../../enums/ReportType';
import { ReportCategoryOrder, ReportTypeCategoryMap } from '../reportCategories';
import { StoreScope } from './StoreScopePicker';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';

interface GenerateReportFormProps {
  reports: ReportNavItem[];
  storeScope: StoreScope | null;
  /** Fired when any report form successfully queues a report; the page refreshes status panels. */
  onGenerated?: () => void;
  /** Notified when the picked report changes so the page can adapt (e.g. hide the scope card). */
  onReportChange?: (report: ReportNavItem | null) => void;
  /** Scope picker rendered directly under the report title + description, above the form. */
  scopeContent?: React.ReactNode;
  /** Advanced scope pickers rendered after the form, under the form's own advanced filters. */
  scopeAdvancedContent?: React.ReactNode;
}

/**
 * Compliance-style single-form report generation: a grouped dropdown (MUI Autocomplete groupBy)
 * picks the report type, then that report's filter form renders inline below it. The shared
 * tenant/franchise/store scope is supplied by the page and threaded into forms that support it.
 */
const GenerateReportForm = ({ reports, storeScope, onGenerated, onReportChange, scopeContent, scopeAdvancedContent }: GenerateReportFormProps) => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const [selectedReport, setSelectedReport] = useState<ReportNavItem | null>(null);
  const [notification, setNotification] = useState<{
    message: string;
    severity: 'success' | 'error';
  } | null>(null);

  const handleReportChange = (report: ReportNavItem | null) => {
    setSelectedReport(report);
    onReportChange?.(report);
  };

  // Only reports that have a V2 category mapping appear in the grouped dropdown.
  const groupedOptions = useMemo(
    () =>
      [...reports]
        .filter((r) => ReportTypeCategoryMap[r.title as ReportType])
        .sort((a, b) => {
          const ca = ReportTypeCategoryMap[a.title as ReportType]!;
          const cb = ReportTypeCategoryMap[b.title as ReportType]!;
          const categoryOrder = ReportCategoryOrder.indexOf(ca) - ReportCategoryOrder.indexOf(cb);
          return categoryOrder || t(a.title).localeCompare(t(b.title));
        }),
    [reports, t],
  );

  // Wait until the scope picker has emitted its default before rendering a scoped form — otherwise
  // the form's legacy store selector flashes while storeScope is still null. The User report is
  // franchise-scoped and gets no storeScope, so it renders immediately.
  const scopeReady = storeScope !== null || selectedReport?.title === ReportType.UserReport;

  const handleGenerated = () => {
    setNotification({
      message: t('reporting.generic.post-generation-message'),
      severity: 'success',
    });
    onGenerated?.();
  };

  const handleGenerationFailed = (message?: string) => {
    setNotification({
      message: message || t('reportingV2.generation-failed'),
      severity: 'error',
    });
  };

  const formContent = scopeReady && selectedReport?.form
    ? cloneElement(selectedReport.form, {
      onGenerated: handleGenerated,
      onGenerationFailed: handleGenerationFailed,
      storeScope: storeScope ?? undefined,
      advancedScopeContent: scopeAdvancedContent,
    })
    : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <Autocomplete
        options={groupedOptions}
        value={selectedReport}
        groupBy={(option) => t(`reportingV2.categories.${ReportTypeCategoryMap[option.title as ReportType]}`)}
        getOptionLabel={(option) => t(option.title)}
        isOptionEqualToValue={(option, value) => option.title === value.title}
        onChange={(_e, value) => handleReportChange(value)}
        // Solid list background + non-sticky group headers prevent options bleeding through the
        // sticky header while the dropdown scrolls.
        ListboxProps={{
          sx: {
            bgcolor: theme.palette.common.white,
            '& .MuiListSubheader-root': { position: 'relative' },
          },
        }}
        renderGroup={(params) => (
          <li key={params.key}
            style={{ listStyle: 'none' }}>
            <ListSubheader
              component="div"
              sx={{
                bgcolor: theme.palette.custom.gray[100],
                fontWeight: 600,
                color: theme.palette.custom.gray[800],
                lineHeight: '40px',
                position: 'relative',
                zIndex: 1,
              }}>
              {params.group}
            </ListSubheader>
            <ul style={{ padding: 0, margin: 0, listStyle: 'none', backgroundColor: theme.palette.common.white }}>{params.children}</ul>
          </li>
        )}
        renderOption={(props, option) => (
          <li {...props}
            key={option.title}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
              <SvgIcon sx={{ width: '18px' }}>{option.icon}</SvgIcon>
              <Typography variant="textMD">{t(option.title)}</Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField {...params}
            size="small"
            label={t('reportingV2.select-report')} />
        )}
        noOptionsText={t('reportingV2.no-reports-found')}
      />

      {selectedReport && (
        <Box sx={{ mt: '4px' }}>
          {/* The report name already shows in the dropdown, so only the description is rendered here. */}
          <Typography variant="textSM"
            sx={{ color: theme.palette.custom.gray[600], mb: '12px', display: 'block' }}>
            {t(selectedReport.info)}
          </Typography>
          {scopeContent && (
            <Box sx={{ mb: '16px' }}>
              {scopeContent}
            </Box>
          )}
          {/* Scoped forms wait for a complete scope; if the user cleared tenant/franchise, say so
              instead of the form silently vanishing or running against a stale scope. */}
          {!scopeReady && (
            <Alert severity="warning"
              sx={{ mt: '8px' }}>
              {t('reportingV2.scope-required')}
            </Alert>
          )}
          {formContent}
        </Box>
      )}
      <MessagesSnackbar
        open={notification !== null}
        onClose={() => setNotification(null)}
        message={notification?.message ?? ''}
        severity={notification?.severity ?? 'success'}
        duration={6000}
      />
    </Box>
  );
};

export default GenerateReportForm;
